/**
 * Is Valid URL
 *
 * @param str
 * @returns {boolean}
 */
function validURL(str) {
  const pattern = new RegExp(
    '^(https?:\\/\\/)?' + // protocol
    '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
    '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
    '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
    '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
    '(\\#[-a-z\\d_]*)?$',
    'i'
  ) // fragment locator
  return !!pattern.test(str)
}

const TRACKING_PROVIDERS = [
  {
    name: 'UPS',
    url: 'https://www.ups.com/track?tracknum=',
    patterns: [
      /^1Z[0-9A-Z]{16}$/iu,
      /^T[0-9A-Z]{10}$/iu,
      /^[0-9]{9}$/iu,
      /^[0-9]{26}$/iu
    ],
    normalize: (raw) => raw,
  },
  {
    name: 'USPS',
    url: 'https://tools.usps.com/go/TrackConfirmAction?tLabels=',
    patterns: [
      /^(92|93|94|95)[0-9]{20}$/iu,
      /^(92|93|94|95)[0-9]{22}$/iu,
      /^(70|14|23|03)[0-9]{14}$/iu,
      /^(M0|82)[0-9]{8}$/iu,
      /^[A-Z]{2}[0-9]{9}[A-Z]{2}$/iu,
      /\b420\d{27}\b/iu
    ],
    normalize: (raw) => raw,
  },
  {
    name: 'FedEx',
    url: 'https://www.fedex.com/apps/fedextrack/?tracknumbers=',
    patterns: [
      /^[0-9]{12}$/iu,
      /^[0-9]{15}$/iu,
      /^[0-9]{20}$/iu,
      /^[0-9]{22}$/iu,
      /^[0-9]{32}$/iu,
      /^[0-9]{34}$/iu
    ],
    normalize: (raw) => {
      if ((/^[0-9]{32}$/iu).test(raw)) {
        // ASTRA barcode: 12-digit tracking number at positions 17–28
        return raw.slice(16, 28);
      }
      if ((/^[0-9]{34}$/iu).test(raw)) {
        // FDX 1D barcode: 14-digit tracking number at positions 21–34,
        // w/ two leading zeros before Express 12-digit tracking number
        const fullTrackingNumber = raw.slice(-14);
        return fullTrackingNumber.startsWith('00') ? fullTrackingNumber.slice(2, 14) : fullTrackingNumber;
      }
      return raw;
    }
  }
];

/**
 *
 * @param trackingNumber
 * @returns {string|null}
 */
function getTrackingUrl(trackingNumber) {
  if (!trackingNumber || trackingNumber.includes('Transfer')) return null;

  const match = TRACKING_PROVIDERS.find(
    ({patterns}) => patterns.some(
      (regex) => regex.test(trackingNumber)
    )
  );

  return match ? `${match.url}${match.normalize(trackingNumber)}` : null;
}

/**
 *
 * @param Vue
 */
import { formatDate, truncateText as truncateTextUtil, humanizeType as humanizeTypeUtil } from '@/util'
import { formatBalance as formatBalanceUtil } from '@/util/refinery'
import { weightDisplayFilter } from '@/util/refinery/weightDisplay'
import { DEFAULT_CURRENCY_PRECISION, DEFAULT_EXTENDED_CURRENCY_PRECISION } from '@/util/refinery/constants'
import { formatToPrecision, formatToPrecisionMetal } from '@/util/formatting'
import { sanitizeMemo } from '@/util/refinery/metalTransactionMemo'
import store from '@/store'

const applyFilters = (Vue) => {

  Vue.filter('trackingLink', function (trackingNumber) {
    const trackingNumberLink = getTrackingUrl(trackingNumber)
    return (trackingNumberLink)
      ? `<a href="${trackingNumberLink}" target="_blank" rel="external">${trackingNumber}</a>`
      : `<span>${trackingNumber}</span>`
  })

  Vue.filter('condense', function (value) {
    const BR_TAG = `<br />`
    return value.replace(/[\r\n]+/g, '\n').replace(/:\r\n|\r|\n/g, BR_TAG)
  })

  Vue.filter('toAddress', function (address) {
    let addressString = ``
    if (Object.keys(address).length > 0 && address.location && address.id) {
      if (address.location.addressee)
        addressString += `${address.location.addressee}<br />`
      if (address.location.attention)
        addressString += `Attn: ${address.location.attention}<br />`
      addressString += `${address.location.street1}${
        address.location.street2
          ? '<br />' + address.location.street2
          : '<br />'
      } ${address.location.city} ${address.location.state} ${
        address.location.zip
      }`
    }
    return addressString
  })

  Vue.filter('toNumberFormat', function (value) {
    if (typeof value !== 'number') {
      value = parseFloat(value)
    }
    const formatter = new Intl.NumberFormat('en-US')
    return !isNaN(value) ? formatter.format(value) : ``
  })

  Vue.filter('toCurrency', function (value) {
    if (typeof value !== 'number') {
      value = parseFloat(value)
    }
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: DEFAULT_CURRENCY_PRECISION,
    })
    return !isNaN(value) ? formatter.format(value) : ``
  })

  Vue.filter('toCurrencyPrecise', function (value) {
    if (typeof value !== 'number') {
      value = parseFloat(value)
    }
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: DEFAULT_EXTENDED_CURRENCY_PRECISION,
      maximumFractionDigits: DEFAULT_EXTENDED_CURRENCY_PRECISION,
    })
    return !isNaN(value) ? formatter.format(value) : ``
  })

  Vue.filter('toDate', function (value) {
    if (!value || value.indexOf(`-`) === -1) return value
    const parts = value.split(`-`)
    const year = parts[0]
    const month = parts[1]
    const day = parts[2]
    return `${parseInt(month)}/${parseInt(day)}/${year}`
  })

  Vue.filter('toUrl', function (url) {
    if (url.length === 0) return `N/A`
    if (validURL(url)) {
      return `<a href="${url}" target="_blank">${url}</a>`
    } else {
      return url
    }
  })

  Vue.filter('toPhone', function (phoneNumberString) {
    let cleaned = ('' + phoneNumberString).replace(/\D/g, '')
    if (cleaned.length === 11 && cleaned.substr(0, 1) === `1`)
      cleaned = cleaned.substr(1, 10)
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
    if (match) {
      return '(' + match[1] + ') ' + match[2] + '-' + match[3]
    }
    return phoneNumberString
  })

  Vue.filter('toPercentage', function (value, fallback = '') {
    if (value === null || value === undefined || isNaN(Number(value))) return fallback
    return `${(Number(value) * 100).toFixed(2)}%`
  })

  const formattedDate = function (date) {
    return date.getMonth() + 1 + '/' + date.getDate() + '/' + date.getFullYear()
  }

  Vue.filter('formattedDate', formattedDate)

  Vue.filter('formattedDateAndTime', function (date) {
    const n = formattedDate(date)
    const time = date.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    })
    return `${n} ${time}`
  })

  // === Additional platform-gold formatters ===
  Vue.filter('formatDateShort', function (val) {
    if (!val) return '';
    return formatDate(new Date(val));
  });

  Vue.filter('humanizeType', function (val) {
    return humanizeTypeUtil(val);
  });

  Vue.filter('truncate', function (val, limit = 50) {
    return truncateTextUtil(val, limit);
  });

  Vue.filter('formatBalance', function (val, unit) {
    if (val === undefined || val === null) return ' ';
    return formatBalanceUtil(val, unit);
  });

  // Weight display filter using centralized policy
  Vue.filter('weightDisplay', weightDisplayFilter);

  // Precision filter for consistent decimal formatting
  Vue.filter('toPrecision', formatToPrecision);

  // Metal-aware precision filter
  // Usage: {{ value | toPrecisionMetal(metalSymbol) }} where metalSymbol ∈ ['AU','AG','PT','PD']
  Vue.filter('toPrecisionMetal', function (value, metalSymbol) {
    if (!metalSymbol) return formatToPrecision(value, 'weight')
    // ensure settings are loaded (no-op if already loaded)
    if (!store.getters || !store.getters.refinerySettingsLoaded) {
      if (store && typeof store.dispatch === 'function') {
        const maybePromise = store.dispatch('fetchRefinerySettings')
        if (maybePromise && typeof maybePromise.catch === 'function') {
          maybePromise.catch(() => {})
        }
      }
    }
    const settings = store.getters && store.getters.refinerySettings ? store.getters.refinerySettings : {}
    return formatToPrecisionMetal(value, metalSymbol, { settings })
  })

  // Sanitize memo text by removing trailing random sequence numbers / GUID-like tokens
  // Examples to strip:
  // Remove auto-generated IDs from memos (UUIDs only, keep reference numbers)
  // Example: "Cash advance 99a7210b-7b4e-404b... (Ref: 123)" -> "Cash advance (Ref: 123)"
  // Example: "Metal settlement 8898925a-..." -> "Metal settlement"
  Vue.filter('sanitizeMemo', sanitizeMemo);

  // Replace TOZ with ozt for display (backend uses TOZ, but UI should show ozt)
  Vue.filter('displayUnit', function (val) {
    if (!val || typeof val !== 'string') return val
    return val.replace(/\bTOZ\b/g, 'ozt')
  });

}

export default applyFilters

